import { useEffect } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useData } from "vike-react/useData";
import { Shelf } from "@/components/CollectionManager/Shelf";
import { FundListViewWithHybridData } from "@/components/FundListView/FundListViewWithHybridData";
import { PageLoader } from "@/components/PageLoader/PageLoader";
import { AnalyticsProvider } from "@/components/Providers/AnalyticsProvider";
import { ChakraThemeProvider } from "@/components/Providers/ChakraThemeProvider";
import { IntegrationProvider } from "@/components/Providers/IntegrationProvider";
import { getHeadSetting } from "@/utils/getHeadSetting";
import { usePageContext } from "@/utils/usePageContext";
import { FeaturesProvider, getFeaturesFromInstanceConfig } from "@/services/Features";
import { InstanceConfigProvider } from "@/services/InstanceConfigContext";
import { useLoading } from "@/services/useLoading";
import type { DataServiceSort } from "@/types/DataService";
import type { FilterBucket } from "@/types/FilterBucket";
import type { FilterOptions } from "@/types/FilterOptions";
import type { PageData } from "@/types/PageData";
import type { FundListPageAndBaseQueryParams } from "@/types/UrlQueries";
import type { ShareClassListEndpoint } from "@/types/endpoints/ShareClassListEndpoint";

type DataType = ShareClassListEndpoint["GET"]["response"]["body"];
type QueryType = FundListPageAndBaseQueryParams;

interface FundListPageEntryPointProps {
	filters: Record<string, FilterOptions>;
}

export default function FundListPageEntryPoint({ filters }: FundListPageEntryPointProps) {
	const dataResponse = useData<PageData<DataType, QueryType>>();
	const context = usePageContext();

	const [isAnyScopeLoading, isOurScopeLoading, isOtherScopeLoading] = useLoading("FundListView");

	useEffect(() => {
		const title = getHeadSetting("title", context) || "";
		document.title = title;
	}, [context]);

	if ("err" in dataResponse || dataResponse.isErr) {
		return (
			<Flex>
				<Text>Error in response from API:</Text>
				<code>{JSON.stringify(dataResponse.err)}</code>
			</Flex>
		);
	}

	const { location, channel, language } = dataResponse.query;

	const { pageIndex, pageSize, filterBucket, searchText, tabIndex, shelfPage = "-1" } = dataResponse.query;

	const data = dataResponse.body;
	const initialSort: DataServiceSort[] = [];
	data.pagination.totalItems = data.totalItems; // why do we do this? can't we modify the api response?

	// add filters in filterSummaries
	data.filterSummaries = data.filterSummaries.map((filterSummary) => {
		return {
			...filterSummary,
			collapsed: filters[filterSummary.name]?.collapsed || false
		};
	});

	const features = getFeaturesFromInstanceConfig(data.instanceConfig);

	return (
		<ChakraThemeProvider overrides={data.themeOverrides}>
			<IntegrationProvider>
				<AnalyticsProvider config={data.analytics}>
					<FeaturesProvider value={features}>
						<InstanceConfigProvider value={data.instanceConfig}>
							{isOtherScopeLoading && <PageLoader />}
							{!isOtherScopeLoading && (
								<FundListViewWithHybridData
									marginTop="-82px"
									location={location}
									channel={channel}
									language={language}
									isLoading={isOurScopeLoading}
									initialTabIndex={tabIndex || "0"}
									initialPageIndex={pageIndex || "0"}
									initialPageSize={pageSize || "40"}
									initialSort={initialSort}
									initialSearchText={searchText || ""}
									initialData={data}
									initialFilterBucket={filterBucket as unknown as FilterBucket}
								/>
							)}
							<Shelf
								initialShelfPage={parseInt(shelfPage, 10)}
								translations={data.translations}
								location={location}
								channel={channel}
								language={language}
							/>
						</InstanceConfigProvider>
					</FeaturesProvider>
				</AnalyticsProvider>
			</IntegrationProvider>
		</ChakraThemeProvider>
	);
}
